import type {
  TodoTemplateRepository,
  OrderTodoRepository,
} from "../../../domain/repositories/company/todo-repository";
import {
  todoTemplateApiService,
  orderTodoApiService,
} from "../../api/company/todo-api-service";
import type {
  TodoTemplate,
  CreateTodoTemplateRequest,
  UpdateTodoTemplateRequest,
  ReorderTodoTemplatesRequest,
  OrderTodo,
  UpdateTodoStatusRequest,
  UpdateTodoAssigneesRequest,
  AddTodoNoteRequest,
  TodoNote,
  CompanyTodosFilter,
} from "../../../domain/types/todo";

// ========== Todo Template Repository Implementation ==========

export class TodoTemplateRepositoryImpl implements TodoTemplateRepository {
  async getTemplates(companyId: number, productId: number): Promise<TodoTemplate[]> {
    const response = await todoTemplateApiService.getTemplates(companyId, productId);
    if (!response.data) {
      throw new Error("템플릿 데이터를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<TodoTemplate> {
    const response = await todoTemplateApiService.getTemplate(companyId, productId, templateId);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 정보를 가져올 수 없습니다.");
  }

  async createTemplate(
    companyId: number,
    productId: number,
    request: CreateTodoTemplateRequest
  ): Promise<TodoTemplate> {
    const response = await todoTemplateApiService.createTemplate(companyId, productId, request);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 생성에 실패했습니다.");
  }

  async updateTemplate(
    companyId: number,
    productId: number,
    templateId: number,
    request: UpdateTodoTemplateRequest
  ): Promise<TodoTemplate> {
    const response = await todoTemplateApiService.updateTemplate(
      companyId,
      productId,
      templateId,
      request
    );
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 수정에 실패했습니다.");
  }

  async reorderTemplates(
    companyId: number,
    productId: number,
    request: ReorderTodoTemplatesRequest
  ): Promise<TodoTemplate[]> {
    const response = await todoTemplateApiService.reorderTemplates(companyId, productId, request);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 순서 변경에 실패했습니다.");
  }

  async toggleActive(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<TodoTemplate> {
    const response = await todoTemplateApiService.toggleActive(companyId, productId, templateId);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 활성화 상태 변경에 실패했습니다.");
  }

  async deleteTemplate(
    companyId: number,
    productId: number,
    templateId: number
  ): Promise<boolean> {
    const response = await todoTemplateApiService.deleteTemplate(companyId, productId, templateId);
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error(response.errorMsg || "템플릿 삭제에 실패했습니다.");
  }
}

// ========== Order Todo Repository Implementation ==========

export class OrderTodoRepositoryImpl implements OrderTodoRepository {
  async getOrderTodos(companyId: number, orderId: number): Promise<OrderTodo[]> {
    const response = await orderTodoApiService.getOrderTodos(companyId, orderId);
    if (!response.data) {
      throw new Error("주문 To-Do 데이터를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getOrderTodo(
    companyId: number,
    orderId: number,
    todoId: number
  ): Promise<OrderTodo> {
    const response = await orderTodoApiService.getOrderTodo(companyId, orderId, todoId);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "To-Do 정보를 가져올 수 없습니다.");
  }

  async getCompanyTodos(
    companyId: number,
    filter?: CompanyTodosFilter
  ): Promise<OrderTodo[]> {
    const response = await orderTodoApiService.getCompanyTodos(companyId, filter);
    if (!response.data) {
      throw new Error("회사 To-Do 데이터를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getMyTodos(companyId: number): Promise<OrderTodo[]> {
    const response = await orderTodoApiService.getMyTodos(companyId);
    if (!response.data) {
      throw new Error("내 To-Do 데이터를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async updateStatus(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoStatusRequest
  ): Promise<OrderTodo> {
    const response = await orderTodoApiService.updateStatus(companyId, orderId, todoId, request);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "To-Do 상태 변경에 실패했습니다.");
  }

  async updateAssignees(
    companyId: number,
    orderId: number,
    todoId: number,
    request: UpdateTodoAssigneesRequest
  ): Promise<OrderTodo> {
    const response = await orderTodoApiService.updateAssignees(companyId, orderId, todoId, request);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "담당자 변경에 실패했습니다.");
  }

  async addNote(
    companyId: number,
    orderId: number,
    todoId: number,
    request: AddTodoNoteRequest
  ): Promise<TodoNote> {
    const response = await orderTodoApiService.addNote(companyId, orderId, todoId, request);
    if (response.data) {
      return response.data;
    }
    throw new Error(response.errorMsg || "메모 추가에 실패했습니다.");
  }
}
